import { type MenuItemType, webMenuData } from '@/menu/webMenu';
import { type Permission, getPermissions, isSuperAdminByRoleId } from '@packages/types/auth';
import { type User } from '@packages/types/auth';
import { supportsLocalUsbBleConfigurator } from '@/utils/browserCapabilities';

type MenuFilterContext = {
  isAuthenticated: boolean;
  permissions: Permission | null;
  isSuperAdmin: boolean;
};

const MENU_PERMISSION_MAP: Record<string, keyof Permission> = {
  monitoring: 'canAccessMonitoring',
  device: 'canAccessDevice',
  company: 'canAccessCompanies',
  site: 'canAccessSites',
  admin: 'canAccessAdmin',
};

const CHILD_PERMISSION_MAP: Record<string, keyof Permission> = {
  companies: 'canAccessAdmin',
  'allowed-emails': 'canAccessAllowedEmails',
  'user-log': 'canViewSystemLogs',
  'system-status': 'canViewSystemLogs',
};

const shouldIncludeTopLevel = (itemId: string, permissions: Permission | null): boolean => {
  const permissionKey = MENU_PERMISSION_MAP[itemId];
  if (!permissionKey) return true;
  return Boolean(permissions?.[permissionKey]);
};

const shouldIncludeChild = (
  itemId: string,
  { permissions }: Pick<MenuFilterContext, 'permissions'>
): boolean => {
  const permissionKey = CHILD_PERMISSION_MAP[itemId];
  if (!permissionKey) return true;
  return Boolean(permissions?.[permissionKey]);
};

/** Local menu items hidden when the user is not signed in (cloud-backed features). */
const GUEST_HIDDEN_LOCAL_CHILD_IDS = new Set(['detaLog']);

const LOCAL_CONFIGURATOR_CHILD_IDS = new Set(['lite', 'mini']);

function stripUnsupportedLocalConfigurator(tree: MenuItemType[]): MenuItemType[] {
  if (supportsLocalUsbBleConfigurator()) return tree;
  return tree
    .map(node => {
      if (node.id === 'local-device' && node.children) {
        const ch = node.children.filter(c => !LOCAL_CONFIGURATOR_CHILD_IDS.has(c.id));
        if (ch.length === 0) return null;
        return { ...node, children: ch };
      }
      return node;
    })
    .filter((n): n is MenuItemType => n !== null);
}

export const buildMenuTree = ({
  isAuthenticated,
  permissions,
  isSuperAdmin: _isSuperAdmin,
}: MenuFilterContext): MenuItemType[] => {
  let tree: MenuItemType[];

  if (!isAuthenticated || !permissions) {
    const localDevice = webMenuData.find(item => item.id === 'local-device');
    if (!localDevice?.children?.length) return [];
    const children = localDevice.children.filter(
      child => !GUEST_HIDDEN_LOCAL_CHILD_IDS.has(child.id)
    );
    tree = children.length > 0 ? [{ ...localDevice, children }] : [];
  } else {
    const filterMenuItems = (items: MenuItemType[]): MenuItemType[] =>
      items
        .map(item => {
          if (!shouldIncludeTopLevel(item.id, permissions)) return null;

          if (!item.children) return item;

          const filteredChildren = filterMenuItems(item.children).filter(child =>
            shouldIncludeChild(child.id, { permissions })
          );

          if (filteredChildren.length === 0) {
            return null;
          }

          return { ...item, children: filteredChildren };
        })
        .filter((item): item is MenuItemType => Boolean(item));

    tree = filterMenuItems(webMenuData);
  }

  return stripUnsupportedLocalConfigurator(tree);
};

export const derivePolicyFromUser = (
  user: User | null
): Pick<MenuFilterContext, 'isAuthenticated' | 'permissions' | 'isSuperAdmin'> => {
  if (!user) {
    return {
      isAuthenticated: false,
      permissions: null,
      isSuperAdmin: false,
    };
  }

  return {
    isAuthenticated: true,
    permissions: getPermissions(user.role_name),
    isSuperAdmin: isSuperAdminByRoleId(user.role_id),
  };
};
